import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import { TbPlus, TbSearch, TbChevronRight, TbLock } from "react-icons/tb";
import { api } from "../lib/api";
import { fmtDate } from "../lib/dates";
import { getAvailableTemplates } from "../../services/templateService";

// The rail's hover panel.
//
// Hovering (or focusing) a rail item opens this beside it with everything for that area:
// its pages, grouped under headings; the "New …" actions; and a column of the area's live
// data — the accounts, the reviews coming up — so the common jobs start from here rather
// than from a page that lists them. It replaced the permanent context column, which
// showed the same links but took ~200px from every screen to do it.
//
// Presentational: ShellV2 owns which panel is open, the hover timing and navigation.

const CONTEXT_LIMIT = 7;

// Context data is fetched when a panel first opens and kept for a minute, so sweeping
// the pointer down the rail does not fire a request per pass. Module-level on purpose:
// the panel unmounts every time it closes, and the cache has to outlive that.
const TTL = 60_000;
const cache = new Map();

function useCached(kind, fetcher) {
  const [state, setState] = useState(() => cache.get(kind)?.data ?? null);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  useEffect(() => {
    if (!fetchRef.current) return undefined;
    const hit = cache.get(kind);
    if (hit) setState(hit.data);
    if (hit && Date.now() - hit.at < TTL) return undefined;
    let live = true;
    fetchRef.current()
      .then((data) => {
        cache.set(kind, { at: Date.now(), data });
        if (live) setState(data);
      })
      .catch(() => {
        // Keep stale data if there is any; a panel that empties on a failed refresh
        // looks like the data was deleted.
        if (live) setState((prev) => prev ?? { error: true });
      });
    return () => { live = false; };
  }, [kind]);

  return state;
}

const FETCHERS = {
  reviews: async () => {
    const data = await api.get("s4/api/review-schedule");
    return (data?.schedule || [])
      .filter((r) => r.next_review || r.overdue)
      // Overdue first, then soonest. That is the order someone would deal with them in.
      .sort((a, b) => (Number(!!b.overdue) - Number(!!a.overdue))
        || String(a.next_review || "").localeCompare(String(b.next_review || "")));
  },
  templates: async () => {
    // Same name-level de-duplication as the Template hub (useTemplates.js): the endpoint
    // returns a template once per copy, and the hub only ever shows one of each name.
    const seen = new Set();
    const all = ((await getAvailableTemplates()) || []).filter((t) => {
      const name = t.name?.toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
    const yours = all.filter((t) => !t.is_community_default && !t.is_pro_default);
    return yours.length ? yours : all;
  },
  leadMagnets: async () => {
    const data = await api.post("/s2/api/lead_magnets", {});
    return data?.widgets || [];
  },
};

const CONTEXT_TITLE = {
  accounts: "Accounts",
  reviews: "Coming up",
  templates: "Your templates",
  leadMagnets: "Your lead magnets",
};

const SEARCH_PLACEHOLDER = {
  accounts: "Search accounts",
  templates: "Search templates",
  leadMagnets: "Search lead magnets",
};

// One row in the context column: a name, and a quiet second line.
const ContextRow = ({ title, meta, tone, onClick }) => (
  <button type="button" className="v2-fly-ctxrow" onClick={onClick}>
    <span className="v2-fly-ctxname">{title}</span>
    {meta && <span className={`v2-fly-ctxmeta${tone ? ` ${tone}` : ""}`}>{meta}</span>}
  </button>
);

const ContextColumn = ({ kind, organizations, onGo, onOpenTemplate, onPickReviewOrg }) => {
  const [q, setQ] = useState("");
  const fetched = useCached(kind, FETCHERS[kind]);
  // Accounts are already loaded by the shell for the nav badge; no second request.
  const data = kind === "accounts" ? organizations : fetched;
  const loading = data == null;
  const failed = !!data?.error;

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const needle = q.trim().toLowerCase();
    const name = (r) => (r.name || r.title || r.organization_name || r.widgetKey || "").toLowerCase();
    const list = needle ? data.filter((r) => name(r).includes(needle)) : data;
    return kind === "accounts" && !needle
      ? [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      : list;
  }, [data, q, kind]);

  const shown = rows.slice(0, CONTEXT_LIMIT);
  const more = rows.length - shown.length;

  const viewAll = {
    accounts: () => onGo("organizations"),
    reviews: () => onGo("review-schedule"),
    templates: () => onGo("templates"),
    // The list is its own screen now, so "view all" goes straight there rather than
    // opening the builder and asking it to switch.
    leadMagnets: () => onGo("widgets"),
  }[kind];

  // Keys carry the index: none of these lists promises a unique id (one organization can
  // appear twice in the schedule, template copies share ids), and a duplicate key makes
  // React reuse the wrong row.
  const renderRow = (r, i) => {
    if (kind === "accounts") {
      return <ContextRow key={`${r.id}-${i}`} title={r.name}
                         onClick={() => onGo("organizations", null, { type: "open", id: r.id })} />;
    }
    if (kind === "reviews") {
      return <ContextRow key={`${r.organization_id}-${i}`} title={r.organization_name}
                         meta={r.overdue ? "Overdue" : `Due ${fmtDate(r.next_review)}`}
                         tone={r.overdue ? "danger" : ""}
                         onClick={() => onPickReviewOrg(r.organization_id)} />;
    }
    if (kind === "templates") {
      return <ContextRow key={`${r.id || r.name}-${i}`} title={r.name}
                         meta={r.is_pro_default ? "Pro default" : r.is_community_default ? "Default" : null}
                         onClick={() => onOpenTemplate(r)} />;
    }
    const leads = Number(r.leadCount) || 0;
    return <ContextRow key={`${r.widgetKey}-${i}`} title={r.title || "Untitled widget"}
                       meta={leads ? `${leads} ${leads === 1 ? "lead" : "leads"}` : "No leads yet"}
                       // The widget key is the builder's route param, so opening one from here is the
                       // same address the saved-widgets screen links to.
                       onClick={() => onGo("lead-magnet", r.widgetKey)} />;
  };

  return (
    <div className="v2-fly-ctx">
      <div className="v2-fly-head">{CONTEXT_TITLE[kind]}</div>
      {SEARCH_PLACEHOLDER[kind] && (
        <label className="v2-fly-search">
          <TbSearch size={13} aria-hidden="true" />
          <input type="search" value={q} placeholder={SEARCH_PLACEHOLDER[kind]}
                 aria-label={SEARCH_PLACEHOLDER[kind]}
                 onChange={(e) => setQ(e.target.value)} />
        </label>
      )}
      <div className="v2-fly-ctxlist">
        {loading ? (
          [0, 1, 2].map((i) => <span key={i} className="v2-fly-skel" />)
        ) : failed ? (
          <p className="v2-fly-empty">Could not load this list.</p>
        ) : shown.length === 0 ? (
          <p className="v2-fly-empty">
            {q ? `Nothing matches “${q}”.` : kind === "reviews" ? "No reviews scheduled." : "Nothing here yet."}
          </p>
        ) : shown.map(renderRow)}
      </div>
      {viewAll && (
        <button type="button" className="v2-fly-all" onClick={viewAll}>
          {more > 0 ? `View all ${rows.length}` : "View all"} <TbChevronRight size={13} />
        </button>
      )}
    </div>
  );
};

const NavFlyout = React.forwardRef(({
  menu, anchor, screen, canSee, isLocked = () => false, organizations,
  onGo, onBuilder, onOpenTemplate, onPickReviewOrg,
  onMouseEnter, onMouseLeave, id,
}, ref) => {
  const [top, setTop] = useState(anchor?.top ?? 0);
  const innerRef = useRef(null);

  // Level with the rail item, then pulled up if it would run off the bottom — the lower
  // rail items would otherwise open a panel half below the fold.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || !anchor) return;
    const max = window.innerHeight - el.offsetHeight - 8;
    setTop(Math.max(8, Math.min(anchor.top - 6, max)));
  }, [anchor, menu?.key]);

  if (!menu || !anchor) return null;

  const sections = menu.sections
    ? menu.sections
        .map((s) => ({ ...s, items: s.items.filter(canSee) }))
        .filter((s) => s.items.length)
    : [{ label: menu.label, items: menu.items }];
  // A locked area keeps its links (each opens the upgrade screen) but loses its "New …"
  // shortcuts and its live-data column: both would only call APIs the plan refuses.
  const quick = menu.locked ? [] : (menu.flyout?.quick || []);

  return (
    <div
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node); else if (ref) ref.current = node;
      }}
      id={id}
      className="v2-fly"
      style={{ top, left: anchor.right + 6 }}
      role="region"
      aria-label={menu.label}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="v2-fly-main">
        <div className="v2-fly-title">{menu.label}</div>

        {quick.length > 0 && (
          <div className="v2-fly-quick">
            {quick.map((a) => (
              <button key={a.label} type="button" className="v2-fly-action"
                      onClick={() => (a.builder ? onBuilder(a.builder) : onGo(a.screen, null, a.intent))}>
                <TbPlus size={14} /> {a.label}
              </button>
            ))}
          </div>
        )}

        {sections.map((section) => (
          <div key={section.label} className="v2-fly-group">
            {sections.length > 1 && <div className="v2-fly-head">{section.label}</div>}
            {section.items.map((item) => {
              const { key, label, icon: Icon } = item;
              const locked = menu.locked || item.locked || isLocked(item);
              return (
                <button key={key} type="button"
                        className={`v2-fly-link${screen === key ? " on" : ""}`}
                        aria-current={screen === key ? "page" : undefined}
                        onClick={() => onGo(key)}>
                  <Icon size={15} /> <span>{label}</span>
                  {locked && <TbLock size={12} className="v2-nav-lock" aria-label="Not in your plan" />}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {menu.flyout?.context && !menu.locked && (
        <ContextColumn
          kind={menu.flyout.context}
          organizations={organizations}
          onGo={onGo}
          onOpenTemplate={onOpenTemplate}
          onPickReviewOrg={onPickReviewOrg}
        />
      )}
    </div>
  );
});

// Whether hovering this menu opens a panel at all. A single-page area with nothing else
// to offer (the dashboard) would open a panel repeating the rail label.
export const hasFlyout = (menu) => !!menu && (menu.items.length > 1 || !!menu.flyout);

export default NavFlyout;
